import { BuilderContent } from "@builder.io/sdk";
import React from "react";

import { GIFT_CARD_LOCALES_IN_NONPROD, GIFT_CARD_LOCALES_IN_PROD } from "@/shared/constants";

import logger from "@/utils/logger";
import { parallel } from "@/utils/promises";
import { isProd } from "@/utils/runtime-environment";
import serverSideTranslations from "@/utils/serverSideTranslations";

import { init } from "@/builder";
import { BuilderModel } from "@/builder/types";

import DefaultLayout from "@/layouts/default";

import { getBuilderMessages, getBuilderNavLinks } from "@/pages/api/builder/builder";
import { getFooterNavigation, getHeaderNavigation } from "@/pages/api/store-directory/navigation";

import GiftCardLayout from "@/components/gift-cards/gift-card-layout";

import { GiftCardProps } from "@/interfaces/gift-cards/gift-card-interface";
import { ChunkedNav, GenericNavItem } from "@/interfaces/navigation-interface";

import { Locale } from "@/types/locale";
import { GetServerSideProps } from "@/types/page";

import { fetchGiftCard } from "../api/store-directory/gift-cards";

interface LocaleProps {
    locale: Locale;
}

type NavProps = {
    headerNavigationItems: ChunkedNav;
    footerLocalNavItems: ChunkedNav;
    footerRegionalNavItems: Array<Array<GenericNavItem>>;
};

type OwnProps = {
    giftCard: GiftCardProps;
    builderMessages: Array<BuilderContent>;
    builderNavLinks: Array<BuilderContent>;
};

type Props = OwnProps & NavProps & LocaleProps;

type Params = {
    giftCardCode: string;
};

init(BuilderModel.PAGE);
init(BuilderModel.MESSAGE);

export const getServerSideProps: GetServerSideProps<Props, Params> = async ({
    locale,
    resolvedUrl,
    params,
}) => {
    if (
        (isProd() && !GIFT_CARD_LOCALES_IN_PROD.includes(locale)) ||
        (!isProd() && !GIFT_CARD_LOCALES_IN_NONPROD.includes(locale))
    ) {
        return {
            notFound: true,
        };
    }

    const giftCard = await fetchGiftCard(params.giftCardCode, locale);
    if (!giftCard) {
        logger.logWarn(`No gift card found for code ${params.giftCardCode}`);
        return {
            notFound: true,
        };
    }

    const parallelProps = await parallel({
        headerNavigationItems: () => getHeaderNavigation(locale),
        footerNavItems: () => getFooterNavigation(locale),
        builderMessages: () => getBuilderMessages(locale, { url: resolvedUrl }),
        builderNavLinks: () => getBuilderNavLinks(locale),
        _nextI18Next: () => serverSideTranslations(locale),
    });

    return {
        props: {
            locale,
            giftCard,
            footerLocalNavItems: parallelProps.footerNavItems[0],
            footerRegionalNavItems: parallelProps.footerNavItems[1],
            ...parallelProps,
        },
    };
};

const GiftCard = ({
    locale,
    headerNavigationItems,
    footerLocalNavItems,
    footerRegionalNavItems,
    giftCard,
    builderMessages,
    builderNavLinks,
}: Props): React.ReactNode => {
    return (
        <DefaultLayout
            currentLocale={locale}
            headerNavigationItems={headerNavigationItems}
            footerLocalNavItems={footerLocalNavItems}
            footerRegionalNavItems={footerRegionalNavItems}
            builderNavLinks={builderNavLinks}
            builderMessages={builderMessages}
        >
            <GiftCardLayout giftCard={giftCard} />
        </DefaultLayout>
    );
};

export default GiftCard;
